import { useQueryClient } from "@tanstack/react-query"
import * as React from "react"

import { type DetectorProfile } from "@/components/domain/detector-profile"
import { useToast } from "@/components/ui/toast"
import { bannerFor } from "@/lib/api-error-message"
import { COMING_SOON, FEATURES } from "@/lib/features"

import {
  resolveNewEngagementFieldError,
  staffRosterQuery,
  useAddClientMemberMutation,
  useCreateClientMutation,
  useRemoveClientMemberMutation,
  useSetEngagementProfileMutation,
  useSetPrimaryClientContactMutation,
} from "../api"
import {
  isPrimaryClientContactNoLongerActiveError,
  PRIMARY_CLIENT_CONTACT_NO_LONGER_ACTIVE,
  type ClientRow,
  type ClientUserOption,
  type CreateClientInput,
  type StaffOption,
  type UserId,
} from "../clients-data"
import type { MemberActions } from "../clients-members"

export interface EngagementActionsOptions {
  /** Called after an engagement is created, so the new row (newest first) is on screen. Every
   *  mutation here already invalidates the engagement list, which refetches on its own. */
  goToFirstPage: () => void
  clientUsers: readonly ClientUserOption[]
  primaryClientContactCandidates: readonly StaffOption[]
  closeNewEngagementDialog: () => void
}

export interface EngagementActions {
  memberActionsFor: (row: ClientRow) => MemberActions
  createEngagement: (draft: CreateClientInput) => Promise<void>
  isCreatingEngagement: boolean
  changePrimaryClientContactFor: (
    row: ClientRow,
  ) => (primaryClientContactId: UserId | null) => Promise<void>
  saveProfileFor: (row: ClientRow) => (profile: DetectorProfile) => Promise<void>
}

export function useEngagementActions({
  goToFirstPage,
  clientUsers,
  primaryClientContactCandidates,
  closeNewEngagementDialog,
}: EngagementActionsOptions): EngagementActions {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const addMemberMutation = useAddClientMemberMutation()
  const removeMemberMutation = useRemoveClientMemberMutation()
  const createClientMutation = useCreateClientMutation()
  const setPrimaryClientContactMutation = useSetPrimaryClientContactMutation()
  const setProfileMutation = useSetEngagementProfileMutation()

  const clientUserName = React.useCallback(
    (userId: UserId) =>
      clientUsers.find((clientUser) => clientUser.id === userId)?.name ?? "Contact",
    [clientUsers],
  )

  const memberActionsFor = React.useCallback(
    (row: ClientRow): MemberActions => ({
      onAddMember: async (userId: UserId) => {
        try {
          await addMemberMutation.mutateAsync({ clientId: row.id, userId })
        } catch (error) {
          const errorBanner = bannerFor(error, "the contact", "save")
          toast({ tone: "error", title: errorBanner.title, body: errorBanner.body })
          throw error
        }
        toast({ tone: "success", title: `${clientUserName(userId)} added to ${row.name}` })
      },
      onRemoveMember: async (userId: UserId) => {
        try {
          await removeMemberMutation.mutateAsync({ clientId: row.id, userId })
        } catch (error) {
          const errorBanner = bannerFor(error, "the contact", "save")
          toast({ tone: "error", title: errorBanner.title, body: errorBanner.body })
          throw error
        }
        toast({ tone: "success", title: `${clientUserName(userId)} removed from ${row.name}` })
      },
      onMakePrimary: () => {
        if (!FEATURES.makePrimaryMember) toast(COMING_SOON)
      },
    }),
    [addMemberMutation, clientUserName, removeMemberMutation, toast],
  )

  const createEngagement = async (draft: CreateClientInput) => {
    const createdEngagement = await createClientMutation
      .mutateAsync(draft)
      .catch(async (createError: unknown) => {
        const fieldError = await resolveNewEngagementFieldError(queryClient, createError, draft)
        if (fieldError !== null) throw fieldError
        const errorBanner = bannerFor(createError, "the engagement", "save")
        toast({ tone: "error", title: errorBanner.title, body: errorBanner.body })
        throw createError
      })
    closeNewEngagementDialog()
    goToFirstPage()
    toast({ tone: "success", title: `${createdEngagement.name} created` })
  }

  const changePrimaryClientContactFor = React.useCallback(
    (row: ClientRow) => async (primaryClientContactId: UserId | null) => {
      try {
        await setPrimaryClientContactMutation.mutateAsync({
          clientId: row.id,
          primaryClientContactId,
        })
      } catch (changeError) {
        if (isPrimaryClientContactNoLongerActiveError(changeError, primaryClientContactId)) {
          toast({ tone: "error", title: PRIMARY_CLIENT_CONTACT_NO_LONGER_ACTIVE })
          void queryClient.invalidateQueries({ queryKey: staffRosterQuery().queryKey })
        } else {
          const errorBanner = bannerFor(changeError, "the engagement", "save")
          toast({ tone: "error", title: errorBanner.title, body: errorBanner.body })
        }
        throw changeError
      }
      const newPrimaryClientContactName = primaryClientContactCandidates.find(
        (staffMember) => staffMember.id === primaryClientContactId,
      )?.name
      toast({
        tone: "success",
        title:
          primaryClientContactId === null
            ? `${row.name} has no Primary Client Contact`
            : `${newPrimaryClientContactName ?? "The new contact"} now gets new reviews for ${row.name}`,
      })
    },
    [primaryClientContactCandidates, queryClient, setPrimaryClientContactMutation, toast],
  )

  const saveProfileFor = React.useCallback(
    (row: ClientRow) => async (profile: DetectorProfile) => {
      try {
        await setProfileMutation.mutateAsync({ clientId: row.id, profile })
      } catch (saveError) {
        const errorBanner = bannerFor(saveError, "the detector profile", "save")
        toast({ tone: "error", title: errorBanner.title, body: errorBanner.body })
        throw saveError
      }
      toast({ tone: "success", title: `Detector profile saved for ${row.name}` })
    },
    [setProfileMutation, toast],
  )

  return {
    memberActionsFor,
    saveProfileFor,
    createEngagement,
    isCreatingEngagement: createClientMutation.isPending,
    changePrimaryClientContactFor,
  }
}
